import type React from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSeoMeta } from "@unhead/react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { RelayItem } from "@/components/RelayItem";
import { NewRelayForm } from "@/components/NewRelayForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  fallbackRelays,
  fallbackRelaysCustomised$,
  gitIndexRelays,
  gitIndexRelaysCustomised$,
  lookupRelays,
  lookupRelaysCustomised$,
  relayCurationMode,
  DEFAULT_GRASP_SERVERS,
  DEFAULT_FALLBACK_RELAYS,
  DEFAULT_LOOKUP_RELAYS,
  DEFAULT_GIT_INDEX_RELAYS,
  defaultNostrConnectRelays,
  nostrConnectRelaysCustomised$,
  DEFAULT_NOSTR_CONNECT_RELAYS,
  type RelayCurationMode,
} from "@/services/settings";
import {
  graspServiceAddressToRelayUrl,
  isValidGraspServiceAddress,
  normalizeGraspServiceAddress,
  validateGraspServer,
} from "@/lib/grasp";
import { use$ } from "@/hooks/use$";
import { useAccount } from "@/hooks/useAccount";
import { useUser } from "@/hooks/useUser";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  walletConnectUri$,
  walletConnect$,
  disconnectWallet,
  setWalletConnectUri,
  hasWebLN,
} from "@/services/wallet";
import { NwcQrConnect } from "@/components/zap/NwcQrConnect";
import { useGraspServers } from "@/hooks/useGraspServers";
import { usePrivateGitRelays } from "@/hooks/usePrivateGitRelays";
import { normalizePrivateGitRelayUrls } from "@/lib/private-git-relays";
import {
  useRobustReplaceableAction,
  type ReplaceablePreflightSnapshot,
} from "@/hooks/useRobustReplaceableAction";
import { useRecoveryToast as useToast } from "@/hooks/useRecoveryToast";
import {
  AddInboxRelayFromPreflight,
  AddOutboxRelayFromPreflight,
  RemoveInboxRelayFromPreflight,
  RemoveOutboxRelayFromPreflight,
} from "@/actions/preflightReplaceableActions";
import {
  GRASP_LIST_KIND,
  ReplaceGraspListFromPreflight,
} from "@/actions/graspListActions";
import { runner } from "@/services/actions";
import { cn } from "@/lib/utils";
import {
  Shield,
  Globe,
  Server,
  Loader2,
  Info,
  Plus,
  RotateCcw,
  Zap,
} from "lucide-react";

const CURATION_OPTIONS: {
  value: RelayCurationMode;
  icon: React.ReactNode;
  title: string;
  description: string;
}[] = [
  {
    value: "repo",
    icon: <Shield className="h-5 w-5" />,
    title: "Curated",
    description:
      "Only events from relays declared in the repository announcement are shown. Maintainers control what appears — spam-resistant and predictable.",
  },
  {
    value: "outbox",
    icon: <Globe className="h-5 w-5" />,
    title: "Uncensored",
    description:
      "Events are fetched from every maintainer's NIP-65 outbox and inbox relays in addition to the repo's declared relays. Nothing is filtered out by relay selection.",
  },
];

function RelayCurationSection() {
  const mode = use$(relayCurationMode);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Event Fetching Strategy</CardTitle>
        <CardDescription>
          Controls which relays are queried when loading issues, patches, and
          comments for a repository.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CURATION_OPTIONS.map((opt) => {
            const selected = mode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => relayCurationMode.next(opt.value)}
                className={cn(
                  "relative flex flex-col gap-2 rounded-lg border p-4 text-left transition-all duration-150",
                  "hover:border-pink-500/50 hover:bg-pink-500/5",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500",
                  selected
                    ? "border-pink-500 bg-pink-500/5 shadow-sm shadow-pink-500/10"
                    : "border-border bg-background",
                )}
              >
                {selected && (
                  <span className="absolute top-3 right-3 h-2 w-2 rounded-full bg-pink-500" />
                )}
                <span
                  className={cn(
                    "transition-colors",
                    selected ? "text-pink-500" : "text-muted-foreground",
                  )}
                >
                  {opt.icon}
                </span>
                <span className="font-semibold text-sm">{opt.title}</span>
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {opt.description}
                </span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function DiscoveryRelaysSection() {
  const lookupRelaysList = use$(lookupRelays);
  const gitIndexRelaysList = use$(gitIndexRelays);
  const lookupCustomised = use$(lookupRelaysCustomised$);
  const gitIndexCustomised = use$(gitIndexRelaysCustomised$);

  const handleAddLookupRelay = (relay: string) => {
    const newRelays = [...new Set([...(lookupRelaysList || []), relay])];
    lookupRelays.next(newRelays);
  };

  const handleRemoveLookupRelay = (relay: string) => {
    const newRelays = (lookupRelaysList || []).filter((r) => r !== relay);
    lookupRelays.next(newRelays);
  };

  const handleAddGitRelay = (relay: string) => {
    const newRelays = [...new Set([...(gitIndexRelaysList || []), relay])];
    gitIndexRelays.next(newRelays);
  };

  const handleRemoveGitRelay = (relay: string) => {
    const newRelays = (gitIndexRelaysList || []).filter((r) => r !== relay);
    gitIndexRelays.next(newRelays);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lookup Relays</CardTitle>
        <CardDescription>
          Relays used to discover users and repositories across the network
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold">Users</h3>
              <p className="text-xs text-muted-foreground">
                Used for discovering user profiles and relay lists
              </p>
            </div>
            {lookupCustomised && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground shrink-0"
                onClick={() => lookupRelays.next([...DEFAULT_LOOKUP_RELAYS])}
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {lookupRelaysList?.map((relay, index) => (
              <RelayItem
                key={index}
                relay={relay}
                onRemove={() => handleRemoveLookupRelay(relay)}
              />
            ))}
          </div>
          <NewRelayForm onAdd={handleAddLookupRelay} />
        </div>

        <div className="border-t pt-6 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold">Repositories</h3>
              <p className="text-xs text-muted-foreground">
                Used for discovering repository announcements published via ngit
              </p>
            </div>
            {gitIndexCustomised && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground shrink-0"
                onClick={() =>
                  gitIndexRelays.next([...DEFAULT_GIT_INDEX_RELAYS])
                }
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {gitIndexRelaysList?.map((relay, index) => (
              <RelayItem
                key={index}
                relay={relay}
                onRemove={() => handleRemoveGitRelay(relay)}
              />
            ))}
          </div>
          <NewRelayForm onAdd={handleAddGitRelay} />
        </div>
      </CardContent>
    </Card>
  );
}

/** kind:10002 — NIP-65 relay list */
const MAILBOXES_KIND = 10002;

function OutboxRelaysSection() {
  const account = useAccount();
  const user = useUser(account?.pubkey);
  const outboxes = use$(user?.outboxes$);
  const { execute } = useRobustReplaceableAction();
  const { toast } = useToast();

  if (!account) return null;

  const safeRun = async (
    action: (snapshot: ReplaceablePreflightSnapshot) => Promise<void>,
  ) => {
    try {
      await execute(MAILBOXES_KIND, action);
    } catch (err) {
      toast({
        recovery: { action: () => safeRun(action) },
        title: "Failed to update relay list",
        description:
          err instanceof Error ? err.message : "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Outbox Relays</CardTitle>
        <CardDescription>
          Relays where your events are published for others to find
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {outboxes?.map((outbox, index) => (
            <RelayItem
              key={index}
              relay={outbox}
              onRemove={() =>
                safeRun(({ event }) =>
                  runner.run(RemoveOutboxRelayFromPreflight, event, outbox),
                )
              }
            />
          ))}
        </div>
        <NewRelayForm
          onAdd={(relay) =>
            safeRun(({ event }) =>
              runner.run(AddOutboxRelayFromPreflight, event, relay),
            )
          }
        />
      </CardContent>
    </Card>
  );
}

function InboxRelaysSection() {
  const account = useAccount();
  const user = useUser(account?.pubkey);
  const inboxes = use$(user?.inboxes$);
  const { execute } = useRobustReplaceableAction();
  const { toast } = useToast();

  if (!account) return null;

  const safeRun = async (
    action: (snapshot: ReplaceablePreflightSnapshot) => Promise<void>,
  ) => {
    try {
      await execute(MAILBOXES_KIND, action);
    } catch (err) {
      toast({
        recovery: { action: () => safeRun(action) },
        title: "Failed to update relay list",
        description:
          err instanceof Error ? err.message : "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Inbox Relays</CardTitle>
        <CardDescription>
          Relays used by other users to send you events
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {inboxes?.map((inbox, index) => (
            <RelayItem
              key={index}
              relay={inbox}
              onRemove={() =>
                safeRun(({ event }) =>
                  runner.run(RemoveInboxRelayFromPreflight, event, inbox),
                )
              }
            />
          ))}
        </div>
        <NewRelayForm
          onAdd={(relay) =>
            safeRun(({ event }) =>
              runner.run(AddInboxRelayFromPreflight, event, relay),
            )
          }
        />
      </CardContent>
    </Card>
  );
}

function GraspRelaysSection() {
  const account = useAccount();
  const pubkey = account?.pubkey;
  const { servers, isFromUserList, isLoading, sourceEvent } =
    useGraspServers(pubkey);
  const { execute } = useRobustReplaceableAction();
  const { toast } = useToast();

  // ---------------------------------------------------------------------------
  // Draft state — local edits before the user hits Save
  // ---------------------------------------------------------------------------

  // null = no draft open (showing published state)
  const [draftAddresses, setDraftAddresses] = useState<string[] | null>(null);
  const draftBaseEventId = useRef<string | null>(null);

  // Sync draft when the published list changes from underneath us (e.g. first
  // load), but only if the user hasn't started editing yet.
  const prevServersRef = useRef(servers);
  useEffect(() => {
    if (prevServersRef.current !== servers) {
      prevServersRef.current = servers;
      // If no draft is open, nothing to do. If a draft is open we leave it
      // alone — the user is mid-edit.
    }
  }, [servers]);

  const activeAddresses =
    draftAddresses ?? servers.map((server) => server.serviceAddress);

  const isDirty =
    draftAddresses !== null &&
    (draftAddresses.length !== servers.length ||
      draftAddresses.some(
        (address, index) => address !== servers[index]?.serviceAddress,
      ));

  const openDraft = useCallback(
    (initial: string[]) => {
      draftBaseEventId.current = sourceEvent?.id ?? null;
      setDraftAddresses([...initial]);
    },
    [sourceEvent?.id],
  );

  const discardDraft = useCallback(() => {
    draftBaseEventId.current = null;
    setDraftAddresses(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Add-server input with 1.5 s debounce auto-validation
  // ---------------------------------------------------------------------------

  const [customAddress, setCustomAddress] = useState("");
  const [customAddressError, setCustomAddressError] = useState<
    string | undefined
  >();
  // "idle" | "validating" | "valid" | "invalid"
  const [validationState, setValidationState] = useState<
    "idle" | "validating" | "valid" | "invalid"
  >("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runValidation = useCallback(async (address: string) => {
    setValidationState("validating");
    setCustomAddressError(undefined);
    const err = await validateGraspServer(address);
    if (err) {
      setValidationState("invalid");
      setCustomAddressError(err);
    } else {
      setValidationState("valid");
    }
  }, []);

  const handleAddressChange = useCallback(
    (raw: string) => {
      setCustomAddress(raw);
      setCustomAddressError(undefined);
      setValidationState("idle");

      if (debounceRef.current) clearTimeout(debounceRef.current);

      const address = normalizeGraspServiceAddress(raw);

      if (!address || !isValidGraspServiceAddress(address)) return;

      debounceRef.current = setTimeout(() => {
        void runValidation(address);
      }, 1500);
    },
    [runValidation],
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleAddAddress = useCallback(async () => {
    const address = normalizeGraspServiceAddress(customAddress);
    if (!address) return;

    if (!isValidGraspServiceAddress(address)) {
      setCustomAddressError(
        "Enter a valid service address (e.g. relay.example.com/grasp)",
      );
      setValidationState("invalid");
      return;
    }

    if (activeAddresses.includes(address)) {
      setCustomAddressError("Already in the list");
      setValidationState("invalid");
      return;
    }

    // If debounce validation is still in flight, cancel it and run immediately
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (validationState !== "valid") {
      await runValidation(address);
      // Re-read state via closure won't work — check error after await
      const err = await validateGraspServer(address);
      if (err) return; // runValidation already set the error
    }

    // Open draft if not already open, then append
    if (draftAddresses === null) {
      draftBaseEventId.current = sourceEvent?.id ?? null;
    }
    setDraftAddresses((previous) => {
      const base = previous ?? servers.map((server) => server.serviceAddress);
      return [...base, address];
    });
    setCustomAddress("");
    setCustomAddressError(undefined);
    setValidationState("idle");
  }, [
    customAddress,
    activeAddresses,
    validationState,
    runValidation,
    servers,
    draftAddresses,
    sourceEvent?.id,
  ]);

  const handleRemoveAddress = useCallback(
    (address: string) => {
      if (draftAddresses === null) {
        draftBaseEventId.current = sourceEvent?.id ?? null;
      }
      setDraftAddresses((previous) => {
        const base = previous ?? servers.map((server) => server.serviceAddress);
        return base.filter((candidate) => candidate !== address);
      });
    },
    [draftAddresses, servers, sourceEvent?.id],
  );

  // ---------------------------------------------------------------------------
  // Publish
  // ---------------------------------------------------------------------------

  const [publishing, setPublishing] = useState(false);

  const publishGraspList = useCallback(
    async (addresses: string[], expectedEventId: string | null) => {
      if (!account) return;
      setPublishing(true);
      try {
        await execute(
          GRASP_LIST_KIND,
          ({ event, outboxes }) =>
            runner.run(
              ReplaceGraspListFromPreflight,
              event,
              outboxes,
              addresses.map(graspServiceAddressToRelayUrl),
            ),
          { expectedEventId },
        );
        draftBaseEventId.current = null;
        setDraftAddresses(null); // close draft on success
      } catch (err) {
        toast({
          recovery: {
            action: () => publishGraspList(addresses, expectedEventId),
          },
          title: "Failed to update grasp server list",
          description:
            err instanceof Error
              ? err.message
              : "An unexpected error occurred.",
          variant: "destructive",
        });
      } finally {
        setPublishing(false);
      }
    },
    [account, execute, toast],
  );

  const handleSave = useCallback(async () => {
    await publishGraspList(
      draftAddresses ?? activeAddresses,
      draftAddresses === null
        ? (sourceEvent?.id ?? null)
        : draftBaseEventId.current,
    );
  }, [publishGraspList, draftAddresses, activeAddresses, sourceEvent?.id]);

  const handleSaveDefaults = useCallback(async () => {
    await publishGraspList([...DEFAULT_GRASP_SERVERS], sourceEvent?.id ?? null);
  }, [publishGraspList, sourceEvent?.id]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const isInputBusy = validationState === "validating" || publishing;

  // The list to render — draft if open, otherwise published
  const displayAddresses =
    draftAddresses ?? servers.map((server) => server.serviceAddress);

  // True when the user has a published list that differs from the defaults
  const publishedAddresses = servers.map((server) => server.serviceAddress);
  const graspIsNonDefault =
    isFromUserList &&
    (publishedAddresses.length !== DEFAULT_GRASP_SERVERS.length ||
      publishedAddresses.some(
        (address, index) => address !== DEFAULT_GRASP_SERVERS[index],
      ));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1.5">
            <CardTitle>GRASP servers</CardTitle>
            <CardDescription>
              Servers used to host your git repositories via the Grasp protocol
            </CardDescription>
          </div>
          {account && graspIsNonDefault && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground shrink-0 mt-0.5"
              disabled={publishing}
              onClick={() => void handleSaveDefaults()}
            >
              {publishing ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <RotateCcw className="h-3 w-3 mr-1" />
              )}
              Reset
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading your server list...
          </div>
        ) : (
          <>
            {/* No user list notice */}
            {!isFromUserList && draftAddresses === null && (
              <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 px-3 py-2.5 flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                <div className="flex-1 space-y-2">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    You don&apos;t have a GRASP server list yet. These defaults
                    are used:
                  </p>
                  {account && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={publishing}
                      onClick={() => void handleSaveDefaults()}
                    >
                      {publishing ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                      ) : null}
                      Save defaults as my list
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Server list (draft or published) */}
            <div className="space-y-2">
              {displayAddresses.map((address) => {
                const isDefault = DEFAULT_GRASP_SERVERS.includes(address);
                const isUserPublished =
                  isFromUserList &&
                  servers.some((server) => server.serviceAddress === address);
                const isDraftOnly =
                  draftAddresses !== null &&
                  !servers.some((server) => server.serviceAddress === address);
                return (
                  <div
                    key={address}
                    className="flex items-center gap-2 rounded-md border border-border/50 bg-muted/20 px-3 py-2"
                  >
                    <Server className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-mono flex-1">{address}</span>
                    {isDraftOnly && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 h-4"
                      >
                        new
                      </Badge>
                    )}
                    {isDefault && !isUserPublished && !isDraftOnly && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 h-4 text-muted-foreground"
                      >
                        default
                      </Badge>
                    )}
                    {account && (
                      <button
                        type="button"
                        onClick={() => {
                          if (draftAddresses === null) {
                            openDraft(
                              servers.map((server) => server.serviceAddress),
                            );
                          }
                          handleRemoveAddress(address);
                        }}
                        className="text-xs text-muted-foreground hover:text-destructive transition-colors px-1"
                        aria-label={`Remove ${address}`}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
              {displayAddresses.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  No servers selected — add at least one before saving.
                </p>
              )}
            </div>

            {/* Add server input — only when logged in */}
            {account && (
              <div className="space-y-1.5">
                <div className="flex gap-2">
                  <Input
                    placeholder="relay.example.com/grasp"
                    value={customAddress}
                    disabled={isInputBusy}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void handleAddAddress();
                      }
                    }}
                    className={cn(
                      "h-8 text-sm font-mono",
                      validationState === "valid" &&
                        "border-green-500 focus-visible:ring-green-500",
                      validationState === "invalid" && "border-red-500",
                    )}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void handleAddAddress()}
                    disabled={isInputBusy || !customAddress.trim()}
                    className="h-8 px-2.5 shrink-0"
                  >
                    {validationState === "validating" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
                {customAddressError && (
                  <p className="text-xs text-red-500 px-0.5">
                    {customAddressError}
                  </p>
                )}
                {validationState === "valid" && !customAddressError && (
                  <p className="text-xs text-green-600 dark:text-green-400 px-0.5">
                    Server supports GRASP-01
                  </p>
                )}
              </div>
            )}

            {/* Save / Discard — shown when there are unsaved changes */}
            {isDirty && (
              <div className="flex items-center justify-end gap-2 pt-1 border-t">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={discardDraft}
                  disabled={publishing}
                  className="h-8 text-xs"
                >
                  Discard
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void handleSave()}
                  disabled={publishing || displayAddresses.length === 0}
                  className="h-8 text-xs"
                >
                  {publishing ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                  ) : null}
                  Save
                </Button>
              </div>
            )}

            {!account && (
              <p className="text-xs text-muted-foreground">
                Log in to manage your GRASP server list.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function privateRelayInputToUrl(value: string): string {
  const trimmed = value.trim();
  const relayUrl = /^wss?:\/\//i.test(trimmed)
    ? trimmed
    : graspServiceAddressToRelayUrl(normalizeGraspServiceAddress(trimmed));
  return normalizePrivateGitRelayUrls([relayUrl])[0];
}

function PrivateGitRelaysSection() {
  const account = useAccount();
  const { state, retry, save } = usePrivateGitRelays();
  const { toast } = useToast();
  const [draft, setDraft] = useState<{
    baseEventId?: string;
    base: string[];
    next: string[];
  } | null>(null);
  const [input, setInput] = useState("");
  const [inputError, setInputError] = useState<string>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(null);
    setInput("");
    setInputError(undefined);
    setSaving(false);
  }, [state.generation, state.sourceEvent?.id]);

  const published = state.relayUrls;
  const displayed = draft?.next ?? published;
  const dirty =
    draft !== null &&
    (!state.sourceEvent ||
      draft.base.length !== draft.next.length ||
      draft.base.some((url, index) => url !== draft.next[index]));

  const edit = useCallback(
    (update: (urls: string[]) => string[]) => {
      setDraft((current) => {
        const base = current?.base ?? [...published];
        const next = update(current?.next ?? [...published]);
        return {
          baseEventId: current?.baseEventId ?? state.sourceEvent?.id,
          base,
          next: [...new Set(next)].sort(),
        };
      });
    },
    [published, state.sourceEvent?.id],
  );

  const add = useCallback(() => {
    try {
      const relayUrl = privateRelayInputToUrl(input);
      if (displayed.includes(relayUrl)) {
        setInputError("Already in the list");
        return;
      }
      edit((urls) => [...urls, relayUrl]);
      setInput("");
      setInputError(undefined);
    } catch {
      setInputError(
        "Enter a ws:// or wss:// relay URL, or a Git service address",
      );
    }
  }, [displayed, edit, input]);

  const persist = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await save(draft.next, draft.baseEventId);
      setDraft(null);
      toast({
        title: "Private Git service list updated",
        variant: "success",
      });
    } catch (error) {
      toast({
        recovery: { action: () => persist() },
        title: "Failed to update private Git services",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [draft, save, toast]);

  return (
    <Card id="private-git-services" className="scroll-mt-20">
      <CardHeader>
        <CardTitle>Private Git services</CardTitle>
        <CardDescription>
          GRASP-08, Buzz, and other private repository relays. This list is
          encrypted to your active Nostr account and its entries are never
          published as public tags.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!account ? (
          <p className="text-xs text-muted-foreground">
            Log in to decrypt and manage your private Git service list.
          </p>
        ) : state.status === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Checking and decrypting your private service list...
          </div>
        ) : state.status === "unavailable" ? (
          <>
            <div
              className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5"
              role="alert"
            >
              <p className="text-sm font-medium">Private list unavailable</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {state.error ??
                  "The list could not be read safely. Editing is disabled so an unknown list is never replaced with an empty one."}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 h-8 text-xs"
                onClick={retry}
              >
                <RotateCcw className="mr-1.5 h-3 w-3" />
                Retry now
              </Button>
            </div>
            {state.relayUrls.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Continuing to use the last successfully decrypted services for
                  private repository reads. Editing stays disabled until the
                  current list can be decrypted.
                </p>
                {state.relayUrls.map((relayUrl) => (
                  <div
                    key={relayUrl}
                    className="flex items-center gap-2 rounded-md border border-border/50 bg-muted/20 px-3 py-2"
                  >
                    <Shield className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate font-mono text-sm">
                      {relayUrl}
                    </span>
                    <Badge variant="secondary" className="text-[10px]">
                      last decrypted
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="space-y-2">
              {displayed.map((relayUrl) => {
                const isDraftOnly =
                  draft !== null && !draft.base.includes(relayUrl);
                return (
                  <div
                    key={relayUrl}
                    className="flex items-center gap-2 rounded-md border border-border/50 bg-muted/20 px-3 py-2"
                  >
                    <Shield className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate font-mono text-sm">
                      {relayUrl}
                    </span>
                    {isDraftOnly && (
                      <Badge
                        variant="secondary"
                        className="h-4 px-1.5 py-0 text-[10px]"
                      >
                        new
                      </Badge>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        edit((urls) =>
                          urls.filter((candidate) => candidate !== relayUrl),
                        )
                      }
                      disabled={saving}
                      className="px-1 text-xs text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
                      aria-label={`Remove ${relayUrl}`}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
              {displayed.length === 0 && (
                <div className="rounded-lg border border-dashed px-4 py-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Your encrypted private Git service list is empty.
                  </p>
                  {!state.sourceEvent && draft === null && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3 h-8 text-xs"
                      onClick={() =>
                        setDraft({
                          baseEventId: state.sourceEvent?.id,
                          base: [],
                          next: [],
                        })
                      }
                      disabled={saving}
                    >
                      Publish encrypted empty list
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(event) => {
                    setInput(event.target.value);
                    setInputError(undefined);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      add();
                    }
                  }}
                  disabled={saving}
                  placeholder="wss://private.example/relay"
                  className={cn(
                    "h-8 font-mono text-sm",
                    inputError && "border-destructive",
                  )}
                  aria-invalid={Boolean(inputError)}
                  aria-describedby={
                    inputError ? "private-git-relay-input-error" : undefined
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={add}
                  disabled={saving || !input.trim()}
                  className="h-8 shrink-0 px-2.5"
                  aria-label="Add private Git service"
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              {inputError && (
                <p
                  id="private-git-relay-input-error"
                  className="px-0.5 text-xs text-destructive"
                  role="alert"
                  aria-live="polite"
                >
                  {inputError}
                </p>
              )}
              {displayed.length === 0 && (
                <p className="px-0.5 text-xs text-muted-foreground">
                  Saving an empty list publishes an encrypted [] replacement.
                </p>
              )}
            </div>

            {dirty && (
              <div className="flex items-center justify-end gap-2 border-t pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setDraft(null)}
                  disabled={saving}
                  className="h-8 text-xs"
                >
                  Discard
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void persist()}
                  disabled={saving}
                  className="h-8 text-xs"
                >
                  {saving && (
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  )}
                  Save encrypted list
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DefaultNostrConnectRelaysSection() {
  const relaysList = use$(defaultNostrConnectRelays);
  const isCustomised = use$(nostrConnectRelaysCustomised$);

  const handleAdd = (relay: string) => {
    const newRelays = [...new Set([...(relaysList || []), relay])];
    defaultNostrConnectRelays.next(newRelays);
  };

  const handleRemove = (relay: string) => {
    const newRelays = (relaysList || []).filter((r) => r !== relay);
    defaultNostrConnectRelays.next(newRelays);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1.5">
            <CardTitle>Default Remote Signer Relays</CardTitle>
            <CardDescription>
              Rendezvous relays used when connecting a new remote signer
              (NIP-46). These are negotiated at login time and stored in the
              bunker URI — changing them only affects new logins, not existing
              accounts.
            </CardDescription>
          </div>
          {isCustomised && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground shrink-0 mt-0.5"
              onClick={() =>
                defaultNostrConnectRelays.next([
                  ...DEFAULT_NOSTR_CONNECT_RELAYS,
                ])
              }
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {relaysList?.map((relay, index) => (
            <RelayItem
              key={index}
              relay={relay}
              onRemove={() => handleRemove(relay)}
            />
          ))}
        </div>
        <NewRelayForm onAdd={handleAdd} />
      </CardContent>
    </Card>
  );
}

function FallbackRelaysSection() {
  const fallbackRelaysList = use$(fallbackRelays);
  const isCustomised = use$(fallbackRelaysCustomised$);

  const handleAddFallbackRelay = (relay: string) => {
    const newRelays = [...new Set([...(fallbackRelaysList || []), relay])];
    fallbackRelays.next(newRelays);
  };

  const handleRemoveFallbackRelay = (relay: string) => {
    const newRelays = (fallbackRelaysList || []).filter((r) => r !== relay);
    fallbackRelays.next(newRelays);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1.5">
            <CardTitle>Fallback Relays</CardTitle>
            <CardDescription>
              Used as a last resort when no other relay source (outbox, repo
              relays, or git index) is available for fetching or publishing
              events
            </CardDescription>
          </div>
          {isCustomised && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground shrink-0 mt-0.5"
              onClick={() => fallbackRelays.next([...DEFAULT_FALLBACK_RELAYS])}
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {fallbackRelaysList?.map((relay, index) => (
            <RelayItem
              key={index}
              relay={relay}
              onRemove={() => handleRemoveFallbackRelay(relay)}
            />
          ))}
        </div>
        <NewRelayForm onAdd={handleAddFallbackRelay} />
      </CardContent>
    </Card>
  );
}

function LightningWalletSection() {
  const uri = use$(walletConnectUri$);
  const wallet = use$(walletConnect$);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<"qr" | "paste">("qr");
  const weblnDetected = hasWebLN();

  const onSave = () => {
    setError(null);
    try {
      setWalletConnectUri(draft.trim());
      setDraft("");
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid connection string");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />
          Lightning Wallet
        </CardTitle>
        <CardDescription>
          Configure a wallet to zap PRs, issues, and other users. WebLN is
          detected automatically; Nostr Wallet Connect (NIP-47) requires a
          connection string from your wallet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-md border p-3 text-sm">
          <div className="flex items-center gap-2">
            <Badge variant={weblnDetected ? "default" : "secondary"}>
              {weblnDetected ? "Detected" : "Not detected"}
            </Badge>
            <span className="font-medium">WebLN browser extension</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {weblnDetected
              ? "Zap dialogs will offer one-tap payment via your extension (e.g. Alby)."
              : "Install a WebLN provider (Alby, Bitcoin Connect, etc.) to enable one-tap zaps."}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Nostr Wallet Connect</span>
            {uri && !editing && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setDraft(uri);
                    setEditing(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-destructive hover:text-destructive"
                  onClick={() => {
                    disconnectWallet();
                    setEditing(false);
                  }}
                >
                  Disconnect
                </Button>
              </div>
            )}
          </div>

          {uri && !editing && (
            <div className="rounded-md border p-3 space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="default">Connected</Badge>
                {wallet?.service && (
                  <span className="font-mono text-xs text-muted-foreground truncate">
                    {wallet.service.slice(0, 12)}…
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                A wallet connection string is saved. Click Edit to replace it or
                Disconnect to remove it.
              </p>
            </div>
          )}

          {(!uri || editing) && (
            <Tabs
              value={mode}
              onValueChange={(v) => setMode(v as "qr" | "paste")}
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="qr">Scan QR</TabsTrigger>
                <TabsTrigger value="paste">Paste URI</TabsTrigger>
              </TabsList>

              <TabsContent value="qr" className="space-y-2">
                <NwcQrConnect
                  onConnected={() => {
                    setEditing(false);
                    setDraft("");
                    setError(null);
                  }}
                />
              </TabsContent>

              <TabsContent value="paste" className="space-y-2">
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="nostr+walletconnect://..."
                  rows={3}
                  className="font-mono text-xs"
                />
                {error && <p className="text-xs text-destructive">{error}</p>}
                <div className="flex justify-end gap-2">
                  <Button size="sm" onClick={onSave} disabled={!draft.trim()}>
                    Save
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Get a connection string from your wallet (Alby, Mutiny, ZBD,
                  Coinos, etc.). The URI is stored only in your browser's local
                  storage.
                </p>
              </TabsContent>

              {editing && (
                <div className="flex justify-end pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing(false);
                      setDraft("");
                      setError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </Tabs>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash !== "#private-git-services") return;
    document.getElementById("private-git-services")?.scrollIntoView();
  }, [hash]);

  useSeoMeta({
    title: "Settings - ngit",
    description: "Manage relay configurations and application settings.",
    ogImage: "/og-image.png",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    twitterCard: "summary_large_image",
  });

  return (
    <div className="container mx-auto p-6 space-y-8 max-w-4xl">
      <div className="space-y-2">
        <h1 className="text-4xl font-bold">Settings</h1>
        <p className="text-muted-foreground">
          Manage your relay configurations for the application.
        </p>
      </div>

      <RelayCurationSection />
      <LightningWalletSection />
      <GraspRelaysSection />
      <PrivateGitRelaysSection />
      <DiscoveryRelaysSection />
      <DefaultNostrConnectRelaysSection />
      <OutboxRelaysSection />
      <InboxRelaysSection />
      <FallbackRelaysSection />
    </div>
  );
}
